import type { Team } from "@repo/shared-types";
import type { RecordablePart } from "@/lib/results";
import { onTeam } from "@/lib/teams";

/** How a part stands for its athlete, as its card on an event's page is outlined (0070). */
export type PartTone = "ok" | "waiting" | "bad";

/**
 * "ok" with nothing waiting on anybody, "waiting" for the box to verify it,
 * "bad" hidden, null with no result. Read from what is saved — a team part's
 * from the team the athlete is on — and from the api's standing, never
 * worked out here.
 */
export function partTone(part: RecordablePart, teams: Team[]): PartTone | null {
  const result = part.isTeam ? teams.find((team) => onTeam(team) && team.recorded) : part.current;
  if (!result?.recorded) return null;
  // Red whenever the line under it says hidden, which reads `hiddenAt`.
  if (result.hiddenAt) return "bad";
  return standingTone(result.standing);
}

/**
 * The tone of a recorded result's standing, as the api says it: on an
 * event's page from the result, on a feed's card from the part's
 * `viewerStanding` (0083), which is null when nothing is recorded — a part
 * still to do, never asked here.
 */
export function standingTone(standing: string | null): PartTone | null {
  switch (standing) {
    case "hidden":
      return "bad";
    case "unverified":
    case "waiting":
      return "waiting";
    case "verified":
    case "":
    case null:
      // Verified, shown as it is, on no board, or private.
      return "ok";
    default:
      // A standing this build does not know says nothing it could get wrong.
      return null;
  }
}
